import { type FC, Fragment, type MouseEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { autoUpdate, FloatingPortal, flip, offset, type Placement, shift, useFloating } from '@floating-ui/react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { EllipsisHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';
import cx from 'classnames';

import useIsMobile from '~/hooks/useIsMobile';

import { GLASS_CLASSES } from './_styles/glass';
import { BottomSheetDialog } from './BottomSheetDialog';

type ActionMenuActionItemBase = {
  type?: 'action';
  key: string;
  label: string;
  description?: string;
  icon?: FC<{ className?: string }>;
  onClick?: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
};

/**
 * Non-disabled action items must provide either an `onClick` handler or a `to`
 * (Remix Link target); a `disabled` item may omit both (purely informational).
 * When both are set, `to` navigates and `onClick` fires as a side effect
 * (useful for closing a parent drawer, tracking, etc.).
 */
export type ActionMenuActionItem =
  | (ActionMenuActionItemBase & { to: string; onClick?: () => void })
  | (ActionMenuActionItemBase & { to?: undefined; onClick: () => void })
  | (ActionMenuActionItemBase & { to?: undefined; onClick?: undefined; disabled: true });

type ActionMenuDividerItem = {
  type: 'divider';
  key: string;
};

export type ActionMenuItem = ActionMenuActionItem | ActionMenuDividerItem;

type ActionMenuProps = {
  items: ActionMenuItem[];
  triggerIcon?: FC<{ className?: string }>;
  triggerIconClassName?: string;
  triggerLabel?: string;
  triggerVariant?: 'default' | 'glass';
  trigger?: ReactNode;
  triggerClassName?: string;
  menuTitle?: string;
  className?: string;
  placement?: Placement;
  alignmentOffset?: number;
};

const defaultTriggerClass =
  'inline-flex items-center justify-center size-8 rounded-full hover:bg-black/5 transition-colors';
const mobileItemBase = 'flex items-center w-full gap-4 min-h-16 px-5 text-base font-normal leading-6 whitespace-nowrap';
const desktopItemBase =
  'flex w-full items-center gap-2 min-h-10 px-4 text-left text-sm font-medium leading-[14px] tracking-[-0.14px] whitespace-nowrap';
const iconClass = 'flex items-center justify-center size-4 shrink-0';

const isDivider = (item: ActionMenuItem): item is ActionMenuDividerItem => item.type === 'divider';

const hasActionItems = (items: ActionMenuItem[]) => items.some((item) => !isDivider(item));

export const ActionMenu: FC<ActionMenuProps> = ({
  items,
  triggerIcon,
  triggerIconClassName,
  triggerLabel = 'More options',
  triggerVariant = 'default',
  trigger,
  triggerClassName,
  menuTitle,
  className,
  placement = 'bottom-end',
  alignmentOffset = 0,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement,
    middleware: [offset({ mainAxis: 8, alignmentAxis: alignmentOffset }), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  if (!hasActionItems(items)) return null;

  const TriggerIcon = triggerIcon ?? EllipsisHorizontalIcon;
  const triggerClasses = triggerVariant === 'glass' ? GLASS_CLASSES : defaultTriggerClass;
  const defaultIconSize = triggerVariant === 'glass' ? 'size-4' : 'size-6';
  const iconClasses = triggerIconClassName ?? defaultIconSize;
  const triggerButtonClassName = trigger ? (triggerClassName ?? '') : triggerClasses;
  const triggerContent = trigger ?? <TriggerIcon className={iconClasses} />;

  if (isMobile) {
    return (
      <div className={className}>
        <button
          type="button"
          className={triggerButtonClassName}
          aria-label={triggerLabel}
          onClick={() => setIsOpen(true)}
        >
          {triggerContent}
        </button>

        <BottomSheetDialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <div className="relative flex items-center justify-center px-5 pt-5 pb-2">
            <Dialog.Title className="text-base font-semibold text-black">
              {menuTitle ?? t('community.action_menu.actions', 'Actions')}
            </Dialog.Title>
            <button
              type="button"
              className="absolute right-5 flex items-center justify-center size-8 rounded-full hover:bg-black/5"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              <XMarkIcon className="size-5 text-black/50" />
            </button>
          </div>

          <div className="flex flex-col pb-2">
            {items.map((item) => {
              if (isDivider(item)) {
                return <ActionMenuDivider key={item.key} item={item} />;
              }

              return (
                <ActionMenuItemContent
                  key={item.key}
                  item={item}
                  base={mobileItemBase}
                  onAfterClick={() => setIsOpen(false)}
                />
              );
            })}
          </div>
        </BottomSheetDialog>
      </div>
    );
  }

  return (
    <Menu as="div" className={cx('relative', className)}>
      <Menu.Button ref={refs.setReference} className={triggerButtonClassName} aria-label={triggerLabel}>
        {triggerContent}
      </Menu.Button>

      <FloatingPortal>
        <Transition
          as={Fragment}
          enter="transition-opacity ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Menu.Items
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-50 min-w-0 overflow-hidden rounded-3xl border border-black/5 bg-white py-1 shadow-[0_4px_6px_rgba(0,0,0,0.08)] focus:outline-none"
          >
            {items.map((item) => {
              if (isDivider(item)) {
                return <ActionMenuDivider key={item.key} item={item} />;
              }

              return (
                <Menu.Item key={item.key} disabled={item.disabled}>
                  {({ active, close }) => (
                    <ActionMenuItemContent item={item} base={desktopItemBase} active={active} onAfterClick={close} />
                  )}
                </Menu.Item>
              );
            })}
          </Menu.Items>
        </Transition>
      </FloatingPortal>
    </Menu>
  );
};

interface ActionMenuItemContentProps {
  item: ActionMenuActionItem;
  /** Base layout className — mobileItemBase for bottom sheets, desktopItemBase for popover menus. */
  base: string;
  /** Desktop keyboard navigation — Headless UI `Menu.Item` active state highlights the focused row. Mouse hover is handled by `hover:bg-black/5`. */
  active?: boolean;
  /** Fires after a non-disabled click. Closes the menu (bottom sheet on mobile, popover on desktop). */
  onAfterClick?: () => void;
}

function ActionMenuItemContent({ item, base, active = false, onAfterClick }: ActionMenuItemContentProps) {
  const { label, description, icon: Icon, onClick, variant, disabled, to } = item;

  const itemClassName = cx(
    base,
    'transition-colors',
    variant === 'danger' ? 'text-red-600' : 'text-black',
    disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-black/5',
    { 'bg-black/5': active && !disabled, 'py-2.5': description },
  );

  const itemContent = (
    <>
      {Icon && (
        <span className={iconClass}>
          <Icon className="size-4" />
        </span>
      )}
      {description ? (
        <span className="flex min-w-0 max-w-64 flex-col gap-1">
          <span className="truncate">{label}</span>
          <span className="truncate font-normal text-black/50">{description}</span>
        </span>
      ) : (
        <span>{label}</span>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        aria-disabled={disabled || undefined}
        className={itemClassName}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          onClick?.();
          onAfterClick?.();
        }}
      >
        {itemContent}
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      className={itemClassName}
      onClick={
        disabled
          ? undefined
          : () => {
              onClick?.();
              onAfterClick?.();
            }
      }
    >
      {itemContent}
    </button>
  );
}

interface ActionMenuDividerProps {
  item: ActionMenuDividerItem;
}

function ActionMenuDivider({ item }: ActionMenuDividerProps) {
  return <div aria-hidden="true" data-testid={`menu-divider-${item.key}`} className="my-1 border-t border-black/10" />;
}
